import React from 'react';
import styled from 'styled-components';
import {
  Loader as InplayerLoader,
  Typography,
  Colors,
  AnalyticsProps,
  AnalyticsComponent,
} from '@inplayer-org/inplayer-ui';
import { useHistory } from 'react-router';
import { transparentize, darken } from 'polished';

// types
import ItemsState from 'ducks/merchantDucks/items/types';

// utils
import { convertArrayToCamelCase } from 'utils/helpers';
import media from 'utils/mediaQueries';

// components
import { LoaderContainer } from 'components/Loader';
import { Asset, AssetTableData } from 'pages/MerchantPages/Assets/components/AssetsTable';
import CardGrid from 'pages/MerchantPages/Assets/containers/CardGrid';
import AssetsPagination from 'pages/MerchantPages/Assets/components/AssetsPagination';

interface Props {
  handleReactivateAsset: (id: number) => void;
  handlePageClick: (page: number) => void;
  assets: ItemsState;
  paginationPage: number;
  renderHeaderSection: () => Node | JSX.Element;
}

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.4rem;
  margin-top: 2rem;

  ${media.customMaxWidth(1490)} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const ButtonContainer = styled.button<AnalyticsProps>`
  border-radius: 5px;
  padding: 0 1rem;
  display: flex;
  border: none;
  outline: none;
  background-color: ${Colors.skyBlue};
  margin-top: 1.5rem;
  cursor: pointer;
  transition: background-color 0.2s ease-out;
  box-shadow: 0 2px 3px -1px ${transparentize(0.3, Colors.darkGray)};

  &:hover {
    background-color: ${darken(0.07, Colors.skyBlue)};
  }

  &:active {
    background-color: ${darken(0.1, Colors.skyBlue)};
  }
`;

export const StyledButton = styled(Typography)`
  padding: 0.4rem 1rem;
  font-weight: ${({ theme }) => theme.font.weights.medium};
  font-size: ${({ theme }) => theme.font.sizes.small};
  color: ${Colors.white};
`;

export interface AssetGridData extends AssetTableData {
  id: number;
  itemType: AssetTableData['itemType'] & {
    content_type: string;
  };
}

const AssetsGrid: React.FC<Props> = ({
  assets: { collection, total, limit, isFetching },
  handlePageClick,
  paginationPage,
  renderHeaderSection,
  handleReactivateAsset,
}) => {
  const history = useHistory();
  const assetsCollection = convertArrayToCamelCase<Asset, AssetGridData>(collection);
  const totalData = total || assetsCollection.length;

  const handlePageChange = (page: number) => {
    if (handlePageClick) {
      handlePageClick(page);
    }
  };

  return (
    <>
      {renderHeaderSection()}
      <GridWrapper>
        {isFetching ? (
          <LoaderContainer>
            <InplayerLoader />
          </LoaderContainer>
        ) : (
          assetsCollection.map((asset: AssetGridData) => {
            const { id, title, createdAt, updatedAt, itemType, metadata, isActive } = asset;
            const handleButtonOnClick = () => {
              if (isActive) {
                history.push(`/assets/edit/${id}`);
              } else if (handleReactivateAsset) {
                handleReactivateAsset(Number(id));
              }
            };

            const renderButton = () => {
              return (
                <AnalyticsComponent>
                  {({ pages, tracker, merchantId, ip }) => (
                    <ButtonContainer
                      onClick={(e) => {
                        e.preventDefault();
                        handleButtonOnClick();
                        tracker.track({
                          event: 'click',
                          type: 'button',
                          tag: `button_edit_${id}`,
                          pages,
                          merchantId,
                          ip,
                        });
                      }}
                    >
                      <StyledButton variant="p"> {isActive ? 'Edit' : 'Reactivate'}</StyledButton>
                    </ButtonContainer>
                  )}
                </AnalyticsComponent>
              );
            };

            return (
              <CardGrid
                key={id}
                id={id}
                title={title}
                dateCreated={createdAt}
                dateUpdated={updatedAt}
                contentType={itemType.content_type}
                coverImage={metadata?.paywall_cover_photo}
                renderButton={renderButton}
              />
            );
          })
        )}
        {!isFetching && !totalData && <Typography variant="p">No assets found.</Typography>}
      </GridWrapper>
      <AssetsPagination
        totalData={totalData}
        paginationPage={paginationPage}
        handlePageChange={handlePageChange}
        numberOfPagesDisplayed={3}
        limit={limit}
      />
    </>
  );
};

export default AssetsGrid;
